/**
 * FileList - Grid/list view of files and directories
 */

import { useState, useMemo } from 'react';
import type { FileInfo, ViewMode, SortField, SortOrder } from '../../types/commander';

interface FileListProps {
  entries: FileInfo[];
  viewMode: ViewMode;
  selectedPaths: Set<string>;
  onSelect: (path: string, multi: boolean) => void;
  onOpen: (entry: FileInfo) => void;
  onContextMenu: (e: React.MouseEvent, entry: FileInfo) => void;
}

// File type icons
function getFileIcon(entry: FileInfo): JSX.Element {
  if (entry.is_dir) {
    return (
      <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z" />
      </svg>
    );
  }

  const ext = entry.name.split('.').pop()?.toLowerCase() || '';

  // Image
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return (
      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }

  // Code
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'rb', 'go', 'rs', 'java', 'cpp', 'c', 'h'].includes(ext)) {
    return (
      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    );
  }

  // Document
  if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf'].includes(ext)) {
    return (
      <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }

  // Spreadsheet
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return (
      <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    );
  }

  // Archive
  if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) {
    return (
      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    );
  }

  // Default file
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function FileList({
  entries,
  viewMode,
  selectedPaths,
  onSelect,
  onOpen,
  onContextMenu
}: FileListProps) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const sortedEntries = useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      // Directories first
      if (a.is_dir !== b.is_dir) {
        return a.is_dir ? -1 : 1;
      }

      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'size':
          cmp = a.size - b.size;
          break;
        case 'modified':
          cmp = new Date(a.modified_at).getTime() - new Date(b.modified_at).getTime();
          break;
        case 'type':
          const extA = a.name.split('.').pop() || '';
          const extB = b.name.split('.').pop() || '';
          cmp = extA.localeCompare(extB);
          break;
      }

      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [entries, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return (
      <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24">
        {sortOrder === 'asc' ? (
          <path d="M7 14l5-5 5 5H7z" />
        ) : (
          <path d="M7 10l5 5 5-5H7z" />
        )}
      </svg>
    );
  };

  if (entries.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p>This folder is empty</p>
        </div>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="flex-1 overflow-y-auto p-2">
        <div className="grid grid-cols-4 gap-2">
          {sortedEntries.map(entry => (
            <div
              key={entry.path}
              className={`
                p-3 rounded-lg cursor-pointer transition-colors
                ${selectedPaths.has(entry.path)
                  ? 'bg-blue-600/30 border border-blue-500'
                  : 'hover:bg-gray-700/50 border border-transparent'
                }
              `}
              onClick={(e) => onSelect(entry.path, e.ctrlKey || e.metaKey)}
              onDoubleClick={() => onOpen(entry)}
              onContextMenu={(e) => onContextMenu(e, entry)}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 flex items-center justify-center mb-2">
                  {getFileIcon(entry)}
                </div>
                <span className="text-sm text-gray-200 truncate w-full">
                  {entry.name}
                </span>
                {!entry.is_dir && (
                  <span className="text-xs text-gray-500 mt-1">
                    {formatFileSize(entry.size)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="flex-1 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-900">
          <tr>
            <th
              className="text-left px-3 py-2 cursor-pointer hover:text-white"
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center">
                Name
                <SortIcon field="name" />
              </div>
            </th>
            <th
              className="text-right px-3 py-2 w-24 cursor-pointer hover:text-white"
              onClick={() => handleSort('size')}
            >
              <div className="flex items-center justify-end">
                Size
                <SortIcon field="size" />
              </div>
            </th>
            <th
              className="text-right px-3 py-2 w-40 cursor-pointer hover:text-white"
              onClick={() => handleSort('modified')}
            >
              <div className="flex items-center justify-end">
                Modified
                <SortIcon field="modified" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map(entry => (
            <tr
              key={entry.path}
              className={`
                cursor-pointer transition-colors
                ${selectedPaths.has(entry.path)
                  ? 'bg-blue-600/30'
                  : 'hover:bg-gray-700/50'
                }
              `}
              onClick={(e) => onSelect(entry.path, e.ctrlKey || e.metaKey)}
              onDoubleClick={() => onOpen(entry)}
              onContextMenu={(e) => onContextMenu(e, entry)}
            >
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  {getFileIcon(entry)}
                  <span className="text-gray-200 truncate">{entry.name}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-right text-gray-400">
                {entry.is_dir ? '-' : formatFileSize(entry.size)}
              </td>
              <td className="px-3 py-2 text-right text-gray-400">
                {formatDate(entry.modified_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
